import { segment } from '@actions/analytics';
import { getMeUid } from '@api/selectors/auth';
import useExperiments from '@stores/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import { abVariations } from '@utils/abHelper';
import { localStorageAvailable } from '@utils/localStorage';
import pushRoute from '@utils/pushRoute';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useToggleable as useDrawer } from '@stores/toggleables';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useClassroomConsumption from '@api/hooks/class/useClassroomConsumption';
import { getClassTimeConsumed } from '@api/selectors/classroom';
import {
  BLOCKER_DISMISSED,
  BLOCKER_VIEWED,
  BOTTOMSHEET_GOT_IT,
  BOTTOMSHEET_VIEWED,
  CLASS_BLOCKER_ID,
  GET_SUBSCRIPTION,
  LIMIT_EXHAUSTED
} from '../constants';

export default function useLimitingClass(clock, currentClass, onBackCleanup) {
  const timer = useRef(null);
  const [timeConsumed, setTimeConsumed] = useState(false);
  const [timeConsumedBeforePlaying, setTimeConsumedBeforePlaying] =
    useState(false);
  const experimentVariation = useExperiments(
    getExperimentVariation('limiting_consumption_app')
  );
  const isTestVariation =
    experimentVariation && experimentVariation === abVariations.TEST_1;
  const { classroomConsumption } = useClassroomConsumption();
  const freeTimeRemaining = getClassTimeConsumed(classroomConsumption?.data);
  const meUID = useMyInfo(getMeUid);
  const currentClassData = useRef(null);
  const { goal } = currentClassData.current?.programme || { goal: {} };
  useEffect(() => {
    if (currentClass.programme) currentClassData.current = currentClass;
  }, [currentClass]);
  const {
    isOpen: isBlockerOpen,
    closeElement: closePlayerBlocker,
    openElement: openPlayerBlocker
  } = useDrawer(CLASS_BLOCKER_ID);

  const handleBottomSheetView = (skipSegment = false) => {
    setTimeConsumedBeforePlaying(true);
    if (!skipSegment)
      segment.track(BOTTOMSHEET_VIEWED, {
        goal_name: goal.name,
        goal_uid: goal.uid,
        value_offering: 'Watchtime',
        value_offering_cap: '60 minutes'
      });
  };
  const handleBackOnBlocker = () => {
    closePlayerBlocker();
    setTimeConsumedBeforePlaying(true);
    segment.track(BLOCKER_DISMISSED, {
      goal_name: goal.name,
      goal_uid: goal.uid
    });
  };
  const handleGotIt = () => {
    closePlayerBlocker();
    segment.track(BOTTOMSHEET_GOT_IT, {
      goal_name: goal.name,
      goal_uid: goal.uid,
      value_offering: 'Watchtime',
      value_offering_cap: '60 minutes'
    });
  };
  const handleBlockView = (skipSegment = false) => {
    const { author } = currentClassData.current;
    setTimeConsumed(true);
    onBackCleanup();
    openPlayerBlocker();
    if (!skipSegment) {
      segment.track(BLOCKER_VIEWED, {
        goal_name: goal.name,
        goal_uid: goal.uid,
        educator_uid: author.uid,
        educator_username: author.username,
        value_offering: 'Watchtime',
        value_offering_cap: '60 minutes'
      });
      if (localStorageAvailable()) {
        const today = format(new Date(), 'dd.MM.yyyy');
        const storageKey = `limit exhausted-${meUID}`;
        const dailyLimitExhaustedTimes = localStorage.getItem(storageKey);
        let value;
        if (dailyLimitExhaustedTimes === null) {
          value = {
            date: today,
            count: 1
          };
        } else {
          const { date, count } = JSON.parse(dailyLimitExhaustedTimes);
          if (today !== date) {
            value = {
              date: today,
              count: count + 1
            };
          }
        }
        if (value) localStorage.setItem(storageKey, JSON.stringify(value));
        if (value && value.count >= 2)
          segment.track(LIMIT_EXHAUSTED, {
            goal_name: goal.name,
            goal_uid: goal.uid,
            value_offering: 'Watchtime',
            value_offering_cap: '2'
          });
      }
    }
  };

  const handleTimeBlockerView = () => {
    if (!timeConsumedBeforePlaying) handleBlockView();
    else handleBottomSheetView();
  };

  const handleGetSubscription = () => {
    segment.track(GET_SUBSCRIPTION, {
      goal_name: goal.name,
      goal_uid: goal.uid,
      banner_type: !timeConsumedBeforePlaying ? 'CLX Screen' : 'Bottom Sheet',
      value_offering: 'Watchtime',
      value_offering_cap: '60 minutes'
    });
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goal.slug}/${goal.uid}/subscribe`,
      {},
      'goal'
    );
  };

  useEffect(() => {
    if (isTestVariation && freeTimeRemaining !== null) {
      if (freeTimeRemaining <= 0) handleBottomSheetView(true);
      else if (!timer.current) {
        timer.current = setInterval(() => {
          const timeWatched = localStorageAvailable()
            ? localStorage.getItem('freeTimeRemaining')
            : 0;
          if (
            clock.started &&
            clock.totalSeconds >= 0 &&
            parseInt(timeWatched, 10) <= 0
          ) {
            handleBlockView(true);
            clearInterval(timer.current);
            timer.current = null;
          }
        }, 2000);
      }
    }

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestVariation, freeTimeRemaining, currentClassData]);
  return {
    timeConsumed,
    timeConsumedBeforePlaying,
    handleBackOnBlocker,
    handleGotIt,
    handleGetSubscription,
    handleTimeBlockerView,
    isBlockerOpen
  };
}
